import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarDays,
  Download,
  FileSpreadsheet,
  Loader2,
  Scale,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  fetchBilan,
  fetchBilanEvenement,
  fetchEvenementsBureau,
  type BilanAnnee,
  type BilanEvenement,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { nomEdition } from '../utils/evenement';
import { euros } from '../utils/format';
import CourbesBilan from '../components/CourbesBilan';

import { dateLongueFr } from '../utils/dates';
/**
 * Le bilan : ventes contre achats, sur l'exercice et par festivité.
 *
 * Deux questions, deux volets. « Où en est l'année ? » — tout ce qui est
 * entré contre tout ce qui est sorti, N contre N-1, poste par poste. « Que
 * nous a coûté la fête, et que nous a-t-elle rapporté ? » — la même balance
 * sur une festivité, comparée à son édition précédente : c'est ainsi qu'on
 * prévoit la suivante.
 */
export default function BilanPage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const aAcces = aLeDroit('voir_tableau_bord');

  const [cibleId, setCibleId] = useState<number | ''>('');
  const [referenceId, setReferenceId] = useState<number | ''>('');

  const bilan = useQuery({ queryKey: ['bilan'], queryFn: fetchBilan, enabled: aAcces });
  const evenementsQuery = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: aAcces,
  });
  const bilanEvenement = useQuery({
    queryKey: ['bilan-evenement', cibleId, referenceId],
    queryFn: () =>
      fetchBilanEvenement(Number(cibleId), referenceId === '' ? undefined : Number(referenceId)),
    enabled: aAcces && cibleId !== '',
  });

  if (authLoading || (aAcces && bilan.isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!aAcces) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Le bilan dépend de votre fonction au bureau : un administrateur peut
              l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const anneeN = bilan.data?.anneeN ?? new Date().getFullYear();
  const anneeN1 = bilan.data?.anneeN1 ?? anneeN - 1;
  const bilanN = bilan.data?.bilanN;
  const bilanN1 = bilan.data?.bilanN1;
  const evenements: { id: number; nom: string }[] = evenementsQuery.data?.evenements ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Scale className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Bilan</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Ventes contre achats : l'exercice, et chaque festivité comparée à son édition
            précédente.
          </p>
        </div>
      </div>

      {/* ------------------------------- Les courbes ------------------------------- */}
      {(bilan.data?.serie?.length ?? 0) > 1 && (
        <section className="card lg:p-6 space-y-3">
          <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100">
            La trajectoire, année par année
          </h2>
          <CourbesBilan serie={bilan.data!.serie} />
        </section>
      )}

      {/* ------------------------------- L'exercice ------------------------------- */}
      <section className="space-y-3">
        <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100">
          Exercice {anneeN}, comparé à {anneeN1}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CarteAnnee annee={anneeN1} bilan={bilanN1} />
          <CarteAnnee annee={anneeN} bilan={bilanN} accent />
        </div>
      </section>

      {/* ------------------------------ Par festivité ------------------------------ */}
      <section className="space-y-3">
        <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100">
          Par festivité
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Une fête récurrente se compare à son édition précédente — la « Fête votive » de
          cette année à celle de l'an dernier — pour prévoir la suivante.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-sm text-gray-600 dark:text-gray-400 block">
            Festivité
            <select
              className="input mt-1"
              value={cibleId}
              onChange={(e) => {
                setCibleId(e.target.value === '' ? '' : Number(e.target.value));
                setReferenceId('');
              }}
            >
              <option value="">Choisir…</option>
              {evenements.map((ev) => (
                <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600 dark:text-gray-400 block">
            Comparer à
            <select
              className="input mt-1"
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={cibleId === ''}
            >
              <option value="">Automatique — l'édition précédente du même nom</option>
              {evenements
                .filter((ev) => ev.id !== cibleId)
                .map((ev) => (
                  <option key={ev.id} value={ev.id}>{nomEdition(ev.nom, (ev as any).dateDebut)}</option>
                ))}
            </select>
          </label>
        </div>

        {cibleId === '' ? (
          <div className="card lg:p-6 text-center space-y-2">
            <CalendarDays className="w-8 h-8 mx-auto text-gray-300" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Choisissez une festivité pour voir sa balance.
            </p>
          </div>
        ) : bilanEvenement.isPending ? (
          <div className="card p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CarteFestivite
              bilan={bilanEvenement.data?.reference ?? null}
              vide="Aucune édition précédente trouvée : choisissez-la à la main ci-dessus."
            />
            <CarteFestivite bilan={bilanEvenement.data?.cible ?? null} accent />
          </div>
        )}
      </section>

      {/* --------------------------- Reprise des années d'avant --------------------------- */}
      {/* Le bilan est l'endroit où l'absence se voit : la courbe commence à
          l'année de l'inscription, et les fêtes d'avant n'ont pas d'édition
          précédente à qui se comparer. C'est là qu'on propose la reprise. */}
      <section className="card lg:p-6 space-y-3">
        <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-primary-700 shrink-0" />
          Les années d'avant ne sont pas dedans ?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Adhérents, sponsors, événements, comptes des exercices passés : nous les reprenons pour
          vous, <strong>sans supplément</strong>. Il n'y a pas d'import automatique — c'est fait à
          la main à partir du classeur ci-dessous. Une fois repris, les exercices anciens entrent
          dans la courbe et chaque fête retrouve son édition précédente.
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          La gratuité tient à une condition : que le tableau revienne correctement rempli. Ce n'est
          pas une formalité — dix minutes de reprise deviennent trois heures dès qu'une colonne est
          remplie en texte libre. Les cases à choix imposé sont là pour cela.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href="https://shop.gest-asso.com/api/modele-import"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium"
          >
            <Download className="w-4 h-4" />
            Télécharger le classeur de reprise
          </a>
          <a
            href="mailto:assistance@gest-asso.com?subject=Reprise%20de%20donn%C3%A9es"
            className="text-sm text-primary-700 dark:text-primary-300 hover:underline"
          >
            Le renvoyer à assistance@gest-asso.com
          </a>
        </div>
        <p className="text-xs text-gray-400">
          Cinq feuilles — adhérents, sponsors, événements, bureau et bénévoles, comptes passés —
          précédées d'un mode d'emploi, avec un exemple sous chaque colonne.
        </p>
      </section>
    </div>
  );
}

/**
 * Une ligne du compte : le libellé à gauche, le montant à droite.
 */
function Poste({
  libelle,
  montant,
  negatif = false,
  fort = false,
}: {
  libelle: string;
  montant: number;
  negatif?: boolean;
  fort?: boolean;
}) {
  return (
    <div
      className={`flex items-baseline justify-between gap-2 ${
        fort ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-400'
      }`}
    >
      <span className="text-sm">{libelle}</span>
      <span className={`tabular-nums ${negatif ? 'text-red-700 dark:text-red-400' : ''}`}>
        {negatif && montant > 0 ? '− ' : ''}
        {euros.format(montant)}
      </span>
    </div>
  );
}

function Solde({ montant }: { montant: number }) {
  const positif = montant >= 0;

  return (
    <div
      className={`mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between ${
        positif ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
      }`}
    >
      <span className="font-semibold inline-flex items-center gap-1.5">
        {positif ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        Solde
      </span>
      <span className="text-xl font-bold tabular-nums">{euros.format(montant)}</span>
    </div>
  );
}

function CarteAnnee({
  annee,
  bilan,
  accent = false,
}: {
  annee: number;
  bilan?: BilanAnnee;
  accent?: boolean;
}) {
  if (!bilan) return null;

  return (
    <div className={`card lg:p-6 ${accent ? 'border-2 border-primary-200 dark:border-primary-800' : ''}`}>
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Exercice</p>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">{annee}</p>

      <div className="space-y-1.5">
        <Poste libelle="Sponsors (factures payées)" montant={bilan.sponsors} />
        <Poste libelle="Dons" montant={bilan.dons} />
        <Poste libelle="Repas" montant={bilan.repas} />
        <Poste libelle="Concerts" montant={bilan.concerts} />
        <Poste libelle="Total des ventes" montant={bilan.totalVentes} fort />
        <Poste libelle="Achats" montant={bilan.achats} negatif fort />
      </div>

      <Solde montant={bilan.solde} />
    </div>
  );
}

function CarteFestivite({
  bilan,
  accent = false,
  vide,
}: {
  bilan: BilanEvenement | null;
  accent?: boolean;
  vide?: string;
}) {
  if (!bilan) {
    return (
      <div className="card lg:p-6 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 text-center">
        {vide ?? '—'}
      </div>
    );
  }

  return (
    <div className={`card lg:p-6 ${accent ? 'border-2 border-primary-200 dark:border-primary-800' : ''}`}>
      <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">{nomEdition(bilan.nom, bilan.dateDebut)}</p>
      {bilan.dateDebut && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {dateLongueFr(bilan.dateDebut)}
        </p>
      )}

      <div className="space-y-1.5">
        <Poste libelle="Repas" montant={bilan.ventes.repas} />
        <Poste libelle="Concerts" montant={bilan.ventes.concerts} />
        {/* Présent seulement en régime « sponsoring par événement » : en
            régime annuel, un parrainage couvre l'année, pas une soirée. */}
        {bilan.ventes.sponsors !== undefined && (
          <Poste libelle="Sponsors" montant={bilan.ventes.sponsors} />
        )}
        <Poste libelle="Total des ventes" montant={bilan.ventes.total} fort />
        <Poste libelle="Achats" montant={bilan.achats.total} negatif fort />
      </div>

      <Solde montant={bilan.solde} />
    </div>
  );
}
